"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { ArrowRight } from "@/components/icons";
import { AnnouncementChip, TrustBenefitList, ReviewBadges } from "@/components/ui";
import { buttonVariants } from "@/components/ui/button";
import { FREE_TRIAL_LABEL, FREE_TRIAL_URL, ROUTES, STATS } from "@/lib/constants";
import { MAINTENANCE_PATH } from "@/lib/maintenance";
import { cn } from "@/lib/utils";
import { useMaintenanceMode } from "@/hooks/use-maintenance-mode";
import { useAffiliateRef } from "@/hooks/use-affiliate-ref";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useHeroAnnouncementChip } from "@/hooks/use-hero-announcement-chip";
import dynamic from "next/dynamic";

// three.js is a heavy chunk — stream the woven-light weave in after hydration
// so it never blocks the hero's first paint (the aurora backdrop covers the gap).
// Desktop only (see `showWeave` below): below lg the weave has no column of its
// own and just sits behind the copy, so it costs a WebGL context and the whole
// three.js download on exactly the devices least able to spare either.
const WovenLightHero = dynamic(
  () => import("@/components/ui/woven-light-hero").then((m) => m.WovenLightHero),
  { ssr: false }
);

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function ProxyTesterHeroSection() {
  const prefersReducedMotion = useReducedMotion();
  const maintenanceMode = useMaintenanceMode();
  const { announcement, onAnnouncementClick } = useHeroAnnouncementChip("homepage");
  const { ref: affiliateRef } = useAffiliateRef();
  const isBuyMode = !!affiliateRef;
  const trialHref = maintenanceMode
    ? MAINTENANCE_PATH
    : isBuyMode
      ? ROUTES.pricing
      : FREE_TRIAL_URL;

  /* lg is where the hero splits into two columns and the weave gets a half of
     its own; below that it only ever sat behind the copy. Gating the MOUNT
     (not just visibility) means the three.js chunk is never fetched on phones
     or tablets. `false` during SSR, which is already the case here — the weave
     is an `ssr: false` dynamic import, so nothing changes server-side. */
  const showWeave = useMediaQuery("(min-width: 1024px)");

  return (
    <section
      className="relative flex flex-col justify-center overflow-y-clip py-28 sm:min-h-[100svh] sm:py-24 lg:py-28"
      aria-label="Hero"
    >
      {/* Gradient ribbon parked off the top-right corner, fading into the page
          on the left so the headline keeps its contrast. */}
      <div className="hero-ribbon-wrap fade-left" aria-hidden="true">
        <div className="hero-ribbon" />
      </div>

      {/* The weave sits in the right half from lg up, opposite the copy. Below
          lg it isn't rendered at all — see `showWeave`.

          On lg the box is left-1/2 → right-0 of THIS SECTION, i.e. the right
          half of the max-w-7xl framed column — not of the viewport. The weave
          centres itself in whatever box it gets, so bounding it to the frame is
          what puts it in the middle of the right column; a 50vw box would push
          it out past the frame's right hairline on wide screens. */}
      {showWeave && (
        <WovenLightHero className="pointer-events-none absolute inset-y-0 left-1/2 z-0 w-screen -translate-x-1/2 overflow-hidden lg:right-0 lg:w-auto lg:translate-x-0" />
      )}

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Two columns from lg up: the copy owns the left cell and the weave owns
            the right half of the viewport, so the text can never sit on top of
            the animation. Below lg it collapses to one column and the weave runs
            full-bleed behind the copy. */}
        <div className="lg:grid lg:grid-cols-2 lg:gap-8">
          <motion.div
            variants={containerVariants}
            initial="visible"
            animate="visible"
            className="flex max-w-xl flex-col items-center text-center lg:items-start lg:text-left"
          >
            <AnnouncementChip
              href={announcement.href}
              label={announcement.label}
              badge={announcement.badge}
              variant="white"
              onClick={onAnnouncementClick}
            />

            {/* No per-element max-width — the column now sets the measure. */}
            <h1 className="text-foreground mt-5 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl">
              The Proxy Network for AI Agents
            </h1>

            {/* muted-foreground is #5e5f6e on the light theme, which reads washed
              out under the h1 — darken it there only, so dark stays as tuned */}
            <p className="text-muted-foreground light:text-foreground/80 mt-4 text-sm leading-relaxed font-medium sm:text-base">
              Powering AI agents and automation for {STATS.customers} of businesses. Mobile and
              residential proxies — real IPs, built to scale with your scraping and data pipelines.
            </p>

            <div className="mt-3 flex w-full items-center justify-center sm:mt-6 sm:min-h-[56px] lg:justify-start">
              <motion.div
                initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="relative flex flex-col items-center lg:items-start"
              >
                <div className="flex flex-wrap items-center justify-center gap-3 lg:justify-start">
                  <span className="cta-glow-btn-wrap">
                    <Link
                      href={trialHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cta-glow-btn-inner"
                    >
                      {isBuyMode ? "Buy Now" : FREE_TRIAL_LABEL}
                      <ArrowRight size={16} aria-hidden="true" />
                    </Link>
                  </span>

                  <Link
                    href={ROUTES.pricing}
                    className={cn(
                      buttonVariants({ variant: "outline", size: "lg" }),
                      "font-medium"
                    )}
                  >
                    See Pricing
                  </Link>
                </div>

                <TrustBenefitList className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2 lg:justify-start" />
              </motion.div>
            </div>

            <motion.div
              variants={itemVariants}
              className="border-border/50 mt-10 flex w-full max-w-xl flex-wrap items-center justify-center gap-3 border-t pt-6 lg:justify-start"
              aria-label="Review platforms"
            >
              <ReviewBadges />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
